import type { ManagementDialogField } from "./ManagementPageTypes";
import type { DBModel } from "../../models/DBModel";
import { Status } from "../../models/Status";

type FieldDefinition = Omit<ManagementDialogField<DBModel>, "Key"> & {
  Key: string;
};

const fieldsByPage: Record<string, FieldDefinition[]> = {
  Categories: [
    {
      Key: "Code",
      Label: "Category code (optional)",
      Placeholder: "Generated automatically if left blank",
      HelpText: "Leave blank to generate a unique category code.",
      Required: false,
      DisableAfterNew: true,
    },
    { Key: "Name", Label: "Category name", Required: true },
    { Key: "ParentCategoryCode", Label: "Parent category code" },
    { Key: "DisplayOrder", Label: "Display order", Type: "integer" },
    {
      Key: "ImageFile",
      Label: "Category image",
      Type: "image",
      FullWidth: true,
    },
    {
      Key: "Description",
      Label: "Description",
      Type: "textarea",
      FullWidth: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
  ],
  Customers: [
    { Key: "Code", Label: "Customer code", DisableAfterNew: true },
    { Key: "BusinessName", Label: "Business name", Required: true },
    {
      Key: "MailAddress",
      Label: "Email address",
      Type: "email",
      Required: true,
    },
    {
      Key: "Password",
      Label: "Password",
      Type: "text",
      Required: true,
      RemoveAfterNew: true,
    },
    { Key: "PhoneNumber", Label: "Phone number", Type: "tel", Required: true },
    { Key: "CreditLimit", Label: "Credit Limit", Type: "decimal" },
    {
      Key: "DefaultCreditDays",
      Label: "Default CreditD Days",
      Type: "integer",
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
    {
      Key: "MutePreDuePaymentNotification",
      Label: "Mute PreDue Payment Notification",
      Type: "switch",
    },
    {
      Key: "CreditSaleRequiresApproval",
      Label: "Credit Sale Require Approval",
      Type: "switch",
    },

    {
      Key: "AllowCreditSale",
      Label: "Allow Credit Sale",
      Type: "switch",
    },
  ],
  Sales: [
    { Key: "Code", Label: "Sale code", Required: true },
    { Key: "CustomerName", Label: "Customer", Required: true },
    { Key: "Amount", Label: "Amount", Type: "money", Required: true },
    {
      Key: "PaymentMethod",
      Label: "Payment method",
      Type: "select",
      Options: ["Bank transfer", "POS", "Cash", "Credit"],
      Required: true,
    },
    {
      Key: "SoldOn",
      Label: "Sale date",
      Type: "datetime",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: ["Paid", "Pending", "Overdue"],
      Required: true,
    },
  ],
  Orders: [
    { Key: "Code", Label: "Order code", Required: true },
    { Key: "CustomerName", Label: "Customer", Required: true },
    {
      Key: "ItemCount",
      Label: "Number of items",
      Type: "integer",
      Required: true,
    },
    { Key: "Amount", Label: "Order amount", Type: "money", Required: true },
    {
      Key: "OrderedOn",
      Label: "Order date",
      Type: "datetime",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: ["Completed", "Processing", "Pending", "Cancelled"],
      Required: true,
    },
  ],
  Inventory: [
    { Key: "ProductCode", Label: "Product code", Required: true },
    { Key: "ProductName", Label: "Product name", Required: true },
    { Key: "WarehouseName", Label: "Warehouse", Required: true },
    { Key: "Quantity", Label: "Quantity", Type: "integer", Required: true },
    {
      Key: "ReorderLevel",
      Label: "Reorder level",
      Type: "integer",
      Required: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: ["InStock", "LowStock", "OutOfStock"],
      Required: true,
    },
  ],
  Warehouses: [
    { Key: "Code", Label: "Warehouse code", Required: true },
    { Key: "Name", Label: "Warehouse name", Required: true },
    { Key: "Location", Label: "Location", FullWidth: true, Required: true },
    { Key: "ManagerName", Label: "Manager", Required: true },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
  ],
  Users: [
    { Key: "Code", Label: "User code", Required: true },
    { Key: "Name", Label: "Full name", Required: true },
    {
      Key: "MailAddress",
      Label: "Email address",
      Type: "email",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "RoleName",
      Label: "Role",
      Type: "select",
      Options: [
        "Super Administrator",
        "Warehouse Manager",
        "Sales Officer",
        "Inventory Officer",
      ],
      Required: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
  ],
  Roles: [
    { Key: "Code", Label: "Role code", Required: true },
    { Key: "Name", Label: "Role name", Required: true },
    {
      Key: "Description",
      Label: "Description",
      Type: "textarea",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
  ],
  Permissions: [
    {
      Key: "Code",
      Label: "Permission code",
      Placeholder: "PRODUCT.MANAGE",
      Required: true,
    },
    { Key: "Name", Label: "Permission name", Required: true },
    { Key: "Module", Label: "Module", Required: true },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: Object.values(Status),
      Required: true,
    },
    {
      Key: "Description",
      Label: "Description",
      Type: "textarea",
      FullWidth: true,
      Required: true,
    },
  ],
  Income: [
    { Key: "Code", Label: "Income code", Required: true },
    { Key: "Description", Label: "Description", Required: true },
    {
      Key: "Category",
      Label: "Category",
      Type: "select",
      Options: ["Sales", "Investment", "Refund", "Other"],
      Required: true,
    },
    { Key: "Amount", Label: "Amount", Type: "money", Required: true },
    {
      Key: "ReceivedOn",
      Label: "Received date",
      Type: "datetime",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "PaymentMethod",
      Label: "Payment method",
      Type: "select",
      Options: ["Bank transfer", "POS", "Cash", "Credit"],
      Required: true,
    },
    { Key: "Reference", Label: "Reference", Required: true },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: ["Received", "Pending"],
      Required: true,
    },
  ],
  Expenses: [
    { Key: "Code", Label: "Expense code", Required: true },
    { Key: "Description", Label: "Description", Required: true },
    {
      Key: "Category",
      Label: "Category",
      Type: "select",
      Options: ["Stock", "Transport", "Utilities", "Payroll", "Other"],
      Required: true,
    },
    { Key: "Amount", Label: "Amount", Type: "money", Required: true },
    {
      Key: "SpentOn",
      Label: "Expense date",
      Type: "datetime",
      FullWidth: true,
      Required: true,
    },
    {
      Key: "PaymentMethod",
      Label: "Payment method",
      Type: "select",
      Options: ["Bank transfer", "POS", "Cash", "Credit"],
      Required: true,
    },
    { Key: "Reference", Label: "Reference", Required: true },
    {
      Key: "Status",
      Label: "Status",
      Type: "select",
      Options: ["Paid", "Pending"],
      Required: true,
    },
  ],
};

export function getManagementDialogFields<T extends DBModel>(
  title: string,
): ManagementDialogField<T>[] {
  return (fieldsByPage[title] ?? []) as ManagementDialogField<T>[];
}
